import { TeamMemberRole } from '@graphql/generated';

/**
 * sessionStorage key carrying the team the user described during web
 * registration. Registration cannot create the team itself anymore
 * (TA-10009 defers creation to Stripe checkout), and the auth stack unmounts
 * once the token is set, so the values travel through sessionStorage to the
 * app stack instead of React state.
 */
export const PENDING_TEAM_SIGNUP_KEY = 'pending_team_signup';

export type PendingTeamSignup = {
  name: string;
  emails: string[];
  /**
   * The address this registration signed up with. The app stack only consumes
   * a payload whose e-mail matches the user it resolved, so a cached `me` from
   * the account previously signed in to this tab can never open checkout for
   * someone else's team.
   */
  ownerEmail: string;
  /** Epoch ms, for PENDING_TEAM_SIGNUP_TTL_MS. */
  createdAt: number;
};

/**
 * How long an unconsumed payload stays valid. Consumption normally happens
 * within a second of the app stack mounting; the window exists so that a
 * registration whose GetMe never succeeds cannot suppress the onboarding
 * tutorial (or re-open checkout) for the rest of the browser session.
 */
export const PENDING_TEAM_SIGNUP_TTL_MS = 5 * 60 * 1000;

export type PendingTeamMember = { email: string; role: TeamMemberRole };

/** Owner shape the checkout form expects in `values.users`. */
export type PendingTeamOwner = {
  id: string;
  name?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  avatar?: unknown;
  role: TeamMemberRole;
};

/**
 * Values handed to the deferred-creation checkout flow. Intentionally a
 * partial FormValues: create-subscription-stack fills the rest from its own
 * schema defaults.
 */
export type PendingTeamValues = {
  name: string;
  teamMembers: PendingTeamMember[];
  users: PendingTeamOwner[];
  teamId: null;
  skillIds: string[];
  logo: null;
  location: null;
  billingCycle: 'year';
};

export const savePendingTeamSignup = (
  pending: Omit<PendingTeamSignup, 'createdAt'>
): void => {
  const name = pending.name?.trim();
  if (!name) return;
  const payload: PendingTeamSignup = {
    name,
    emails: (pending.emails ?? []).filter((email) => !!email?.trim()),
    ownerEmail: pending.ownerEmail?.trim().toLowerCase() ?? '',
    createdAt: Date.now(),
  };
  window.sessionStorage.setItem(
    PENDING_TEAM_SIGNUP_KEY,
    JSON.stringify(payload)
  );
};

/**
 * Returns the stored signup, or null when it is absent, unparseable or past
 * PENDING_TEAM_SIGNUP_TTL_MS. An expired payload is dropped on read so a
 * stranded one stops holding back the onboarding tutorial.
 */
export const readPendingTeamSignup = (): PendingTeamSignup | null => {
  const raw = window.sessionStorage.getItem(PENDING_TEAM_SIGNUP_KEY);
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed.name !== 'string' || !parsed.name) return null;
    const createdAt =
      typeof parsed.createdAt === 'number' ? parsed.createdAt : 0;
    if (Date.now() - createdAt > PENDING_TEAM_SIGNUP_TTL_MS) {
      clearPendingTeamSignup();
      return null;
    }
    return {
      name: parsed.name,
      emails: Array.isArray(parsed.emails)
        ? parsed.emails.filter((email: unknown) => typeof email === 'string')
        : [],
      ownerEmail:
        typeof parsed.ownerEmail === 'string' ? parsed.ownerEmail : '',
      createdAt,
    };
  } catch {
    return null;
  }
};

/**
 * True when this payload belongs to `userEmail`. A payload written before
 * ownerEmail existed (or by a flow that could not supply it) matches anyone,
 * so an upgrade mid-session does not strand it.
 */
export const isPendingTeamSignupForUser = (
  pending: PendingTeamSignup,
  userEmail?: string | null
): boolean =>
  !pending.ownerEmail || pending.ownerEmail === userEmail?.trim().toLowerCase();

export const clearPendingTeamSignup = (): void => {
  window.sessionStorage.removeItem(PENDING_TEAM_SIGNUP_KEY);
};

/**
 * Maps the stored signup onto the checkout form values, so the team entered
 * at registration reaches Stripe checkout exactly like a team started from
 * the Team page (owner counted once, invitations as deferred members).
 */
export const buildPendingTeamValues = (
  pending: PendingTeamSignup,
  owner?: PendingTeamOwner | null
): PendingTeamValues => ({
  name: pending.name,
  teamMembers: pending.emails.map((email) => ({
    email,
    role: TeamMemberRole.Member,
  })),
  users: owner ? [{ ...owner, role: TeamMemberRole.Owner }] : [],
  teamId: null,
  skillIds: [],
  logo: null,
  location: null,
  billingCycle: 'year',
});
